import {
  Button,
  HStack,
  IconButton,
  Popover,
  Portal,
  Text,
  VStack
} from "@chakra-ui/react"
import type { IconName } from "@pathwright/pathicons"
import { handleKeyControl } from "@pathwright/utils/src/keyboard/useKeyControl"
import {
  LIKERT_AUGMENTATION_MENU_CONTENT_PROPS,
  LIKERT_AUGMENTATION_MENU_ITEM_ICON_CSS,
  LIKERT_AUGMENTATION_OPTIONS_MENU_ITEM_CSS,
  LIKERT_AUGMENTATION_OPTIONS_MENU_ITEM_GAP,
  LIKERT_AUGMENTATION_TYPOGRAPHY_CSS
} from "./augmentation-styles"
import Pathicon from "@pathwright/ux/src/components/pathicon/Pathicon"
import Tooltip from "@pathwright/ux/src/components/tooltip/Tooltip"
import {
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactElement,
  type ReactNode,
  useState
} from "react"

type MenuButtonProps = {
  icon: IconName
  label: string
  disabled?: boolean
  onClick?: () => void
}

const MenuButton = ({ icon, label, disabled, onClick }: MenuButtonProps) => (
  <Button
    type="button"
    variant="plain"
    disabled={disabled}
    onClick={onClick}
    css={LIKERT_AUGMENTATION_OPTIONS_MENU_ITEM_CSS}
    display="flex"
    minW="8rem"
    aria-disabled={disabled || undefined}
    cursor={disabled ? "not-allowed" : "pointer"}
    opacity={disabled ? 0.5 : undefined}
    pointerEvents={disabled ? "none" : undefined}
    _hover={{ textDecor: "none" }}
  >
    <HStack
      align="center"
      gap={LIKERT_AUGMENTATION_OPTIONS_MENU_ITEM_GAP}
      justify="flex-start"
      w="100%"
    >
      <Pathicon
        icon={icon}
        size={16}
        css={LIKERT_AUGMENTATION_MENU_ITEM_ICON_CSS}
      />
      <Text as="span" my={0}>
        {label}
      </Text>
    </HStack>
  </Button>
)

const moveActionFocus = (event: KeyboardEvent, container: HTMLElement) => {
  const actions = Array.from(
    container.querySelectorAll<HTMLButtonElement>(
      "button:not(:disabled):not([data-disabled])"
    )
  )
  if (!actions.length) return

  event.preventDefault()
  const currentIndex = actions.indexOf(
    document.activeElement as HTMLButtonElement
  )
  const direction = event.key === "ArrowDown" ? 1 : -1
  const nextIndex =
    currentIndex === -1
      ? direction === 1
        ? 0
        : actions.length - 1
      : (currentIndex + direction + actions.length) % actions.length

  actions[nextIndex].focus()
}

// Handles keyboard navigation for the action popover
const handleActionKeyDown = (event: ReactKeyboardEvent<HTMLDivElement>) => {
  const container = event.currentTarget
  handleKeyControl(["ArrowDown", "ArrowUp"], (keyboardEvent) => {
    moveActionFocus(keyboardEvent, container)
    return true
  })(event)
}

const ActionPopover = ({
  ariaLabel,
  className,
  trigger,
  canOpen,
  children
}: {
  ariaLabel: string
  className?: string
  trigger?: ReactElement
  canOpen?: () => boolean
  children: (run: (action?: () => void) => () => void) => ReactNode
}) => {
  const [open, setOpen] = useState(false)
  const closeAndRun = (action?: () => void) => () => {
    setOpen(false)
    action?.()
  }

  return (
    <Popover.Root
      open={open}
      onOpenChange={(details) => {
        if (details.open && canOpen && !canOpen()) return
        setOpen(details.open)
      }}
      positioning={{ placement: "bottom-end" }}
    >
      <Popover.Trigger asChild>
        {trigger || (
          <IconButton
            aria-label={ariaLabel}
            className={className}
            size="2xs"
            variant="ghost"
          >
            <Pathicon icon="ellipses-horizontal" />
          </IconButton>
        )}
      </Popover.Trigger>
      <Portal>
        <Popover.Positioner>
          <Popover.Content
            {...LIKERT_AUGMENTATION_MENU_CONTENT_PROPS}
            zIndex="popover"
            css={LIKERT_AUGMENTATION_TYPOGRAPHY_CSS}
            outline="none"
          >
            <VStack align="stretch" gap={0} onKeyDown={handleActionKeyDown}>
              {children(closeAndRun)}
            </VStack>
          </Popover.Content>
        </Popover.Positioner>
      </Portal>
    </Popover.Root>
  )
}

export const ScaleItemMenu = ({
  canDelete,
  onInsertBefore,
  onInsertAfter,
  onDelete
}: {
  canDelete: boolean
  onInsertBefore?: () => void
  onInsertAfter?: () => void
  onDelete: () => void
}) => (
  <ActionPopover
    ariaLabel="Scale item actions"
    className="LikertScaleMenuTrigger"
  >
    {(run) => (
      <>
        <MenuButton
          icon="plus"
          label="Insert before"
          disabled={!onInsertBefore}
          onClick={run(onInsertBefore)}
        />
        <MenuButton
          icon="plus"
          label="Insert after"
          disabled={!onInsertAfter}
          onClick={run(onInsertAfter)}
        />
        {canDelete ? (
          <MenuButton icon="trash" label="Delete" onClick={run(onDelete)} />
        ) : null}
      </>
    )}
  </ActionPopover>
)

export const StatementMenu = ({
  canDelete,
  canMoveUp,
  canMoveDown,
  onDuplicate,
  onMoveUp,
  onMoveDown,
  onDelete,
  trigger,
  canOpen
}: {
  canDelete: boolean
  canMoveUp: boolean
  canMoveDown: boolean
  onDuplicate: () => void
  onMoveUp: () => void
  onMoveDown: () => void
  onDelete: () => void
  trigger: ReactElement
  canOpen?: () => boolean
}) => (
  <ActionPopover
    ariaLabel="Statement actions"
    className="LikertStatementMenuTrigger"
    trigger={trigger}
    canOpen={canOpen}
  >
    {(run) => (
      <>
        {canMoveUp ? (
          <MenuButton
            icon="chevron-up"
            label="Move up"
            onClick={run(onMoveUp)}
          />
        ) : null}
        {canMoveDown ? (
          <MenuButton
            icon="chevron-down"
            label="Move down"
            onClick={run(onMoveDown)}
          />
        ) : null}
        <MenuButton icon="copy" label="Duplicate" onClick={run(onDuplicate)} />
        <MenuButton
          icon="trash"
          label="Delete"
          disabled={!canDelete}
          onClick={run(onDelete)}
        />
      </>
    )}
  </ActionPopover>
)

export const InfoTip = ({
  children,
  label = "More information"
}: {
  children: ReactNode
  label?: string
}) => (
  <Tooltip title={children} content={{ maxWidth: "240px" }}>
    <IconButton aria-label={label} size="2xs" variant="ghost">
      <Pathicon icon="info-circle" />
    </IconButton>
  </Tooltip>
)
